import { useState, FormEvent } from "react";
import IMask from "imask";
import { IMaskInput } from "react-imask";
import PageLayout from "@/components/feature/PageLayout";

const steps = [
  { id: 1, title: "Your Info", icon: "ri-user-line" },
  { id: 2, title: "Transaction", icon: "ri-bank-card-line" },
  { id: 3, title: "Details", icon: "ri-file-text-line" },
  { id: 4, title: "Review", icon: "ri-check-double-line" }
];

export default function DisputeResolution() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<Record<string, string>>({
    name: "",
    email: "",
    date_of_transaction: "",
    amount: "",
    currency: "",
    card_first6: "",
    card_last4: "",
    description: ""
  });

  const currentDay = new Date()

  function updateField(name: string, value: string) {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function validateStep(s: number): boolean {
    const newErrors: Record<string, string> = {};
    if (s === 1) {
      if (!formData.name.trim()) newErrors.name = "Name is required";
      if (
        !formData.email.trim() ||
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
      )
        newErrors.email = "Valid email is required";
    }
    if (s === 2) {
    if (!formData.date_of_transaction.trim()) {
      newErrors.date_of_transaction = "Date is required";
    } else if (formData.date_of_transaction.length < 10) {
      newErrors.date_of_transaction = "Please enter a complete date";
    } else {
      const [dd, mm, yyyy] = formData.date_of_transaction.split('/').map(Number);
      const entered = new Date(yyyy, mm - 1, dd);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      today.setDate(today.getDate() + 1);
      if (entered > today) {
        newErrors.date_of_transaction = "Date cannot be in the future";
      }
    }
      if (!formData.amount.trim() || isNaN(Number(formData.amount))) {
        newErrors.amount = "Valid amount is required";
      } else if (+formData.amount <= 0) {
        newErrors.amount = "Amount must be greater than 0";
      }
      if (!formData.currency) newErrors.currency = "Please select a currency";

      if (!formData.card_first6.trim() || formData.card_first6.length < 6) {
        newErrors.card_first6 = "Must be 6 digits";
      }

      if (!formData.card_last4.trim() || formData.card_last4.length < 4) {
        newErrors.card_last4 = "Must be 4 digits";
      }
    }
    if (s === 3) {
      if (formData.description.trim().length < 20)
        newErrors.description = "Minimum 20 characters required";
      if (formData.description.trim().length > 500)
        newErrors.description = "Maximum 500 characters allowed";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function nextStep() {
    if (validateStep(step)) setStep(s => Math.min(s + 1, 4));
  }

  function prevStep() {
    setStep(s => Math.max(s - 1, 1));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateStep(step)) return;

    setLoading(true);

    let text = "";
    for (const [key, value] of Object.entries(formData)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: formData.email,
      text,
      problemType: "Transaction Issue",
      to_email: "support@kingcraftech.com",
      subject: "Support Request"
    };

    try {
      const res = await fetch(
        "https://rocknlabs.com/api/support/request_form",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
    } catch {
      setErrorSubmit(true);
    } finally {
      setLoading(false);
    }
  }

  const inputClass =
    "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#c4a574] focus:border-[#c4a574] bg-white text-gray-800 placeholder-gray-400 transition-all";

  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  if (submitted) {
    return (
      <PageLayout>
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-[#f5f0e8] rounded-full mx-auto mb-5">
              <i className="ri-check-line text-[#8b7355] text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Dispute Submitted
            </h3>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Thank you! We&apos;ve received your dispute and will review it
              within 2–3 business days. You&apos;ll hear from us via email.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setStep(1);
                setFormData({
                  name: "",
                  email: "",
                  date_of_transaction: "",
                  amount: "",
                  currency: "",
                  card_first6: "",
                  card_last4: "",
                  description: ""
                });
                setCharCount(0);
                setErrors({});
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Submit Another Dispute
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (errorSubmit) {
    return (
      <PageLayout>
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-[#f5f0e8] rounded-full mx-auto mb-5">
              <i className="ri-close-line text-[#8b7355] text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Message Failed
            </h3>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Something went wrong while sending your message. Please check your
              connection and try again.
            </p>
            <button
              onClick={() => {
                setErrorSubmit(false);
                setSubmitted(false);
                setStep(1);
                setFormData({
                  name: "",
                  email: "",
                  date_of_transaction: "",
                  amount: "",
                  currency: "",
                  card_first6: "",
                  card_last4: "",
                  description: ""
                });
                setCharCount(0);
                setErrors({});
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1a1a1a] text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Try Again
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <style>{`
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset;
      }
    `}</style>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center bg-[#f5f0e8] rounded-2xl mx-auto mb-4">
            <i className="ri-shield-check-line text-[#8b7355] text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Dispute Resolution
          </h2>
          <p className="text-gray-500 text-sm">
            Report an issue with a transaction in a few simple steps
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8 px-2">
          {steps.map((s, idx) => (
            <div key={s.id} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step > s.id
                      ? "bg-[#8b7355] text-white"
                      : step === s.id
                        ? "bg-[#1a1a1a] text-white ring-4 ring-gray-100"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {step > s.id ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    <i className={s.icon}></i>
                  )}
                </div>
                <span
                  className={`text-xs font-medium mt-2 ${
                    step >= s.id ? "text-gray-700" : "text-gray-400"
                  }`}
                >
                  {s.title}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-3 transition-all ${
                    step > s.id ? "bg-[#8b7355]" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
          <form onSubmit={handleSubmit} id="dispute-resolution-form">
            {/* Step 1: Your Info */}
            {step === 1 && (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={e => updateField("name", e.target.value)}
                    className={inputClass}
                    placeholder="John Doe"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={e => updateField("email", e.target.value)}
                    className={inputClass}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Transaction */}
            {step === 2 && (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Date of Transaction</label>
                    <IMaskInput
                      mask="DD/MM/YYYY"
                      blocks={{
                        DD: { mask: IMask.MaskedRange, from: 1, to: 31 },
                        MM: { mask: IMask.MaskedRange, from: 1, to: 12 },
                        YYYY: { mask: IMask.MaskedRange, from: 2000, to: currentDay.getFullYear()}
                      }}
                      name="date_of_transaction"
                      value={formData.date_of_transaction}
                      validate={(value: string) => {
                        if (value.length < 10) return true; 
                        const [dd, mm, yyyy] = value.split('/').map(Number);
                        const entered = new Date(yyyy, mm - 1, dd);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        today.setDate(today.getDate() + 1);
                        return entered <= today; 
                      }}
                      onAccept={value =>
                        setFormData({ ...formData, date_of_transaction: value })
                      }
                      className={inputClass}
                      placeholder="DD/MM/YYYY"
                    />
                    {errors.date_of_transaction && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {errors.date_of_transaction}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Amount</label>
                  <IMaskInput
                    mask={Number}
                    scale={2}
                    min={0}
                    max={100}
                    radix="."  
                    mapToRadix={[","]} 
                    normalizeZeros
                    name="amount"
                    value={formData.amount}
                    onAccept={value => updateField("amount", value)}
                    className="w-full border-2 border-black/20 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white rounded-lg"
                    placeholder="0.00"
                  />
                    {errors.amount && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {errors.amount}
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div className="sm:col-span-1">
                    <label className={labelClass}>Currency</label>
                    <div className="relative">
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={e => updateField("currency", e.target.value)}
                        className={`${inputClass} appearance-none pr-10 cursor-pointer`}
                        autoComplete="off"
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="CAD">CAD</option>
                        <option value="AUD">AUD</option>
                        <option value="JPY">JPY</option>
                        <option value="CHF">CHF</option>
                        <option value="CNY">CNY</option>
                        <option value="RUB">RUB</option>
                        <option value="OTHER">Other</option>
                      </select>
                      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </div>
                    </div>
                    {errors.currency && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {errors.currency}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Card (First 6)</label>
                    <IMaskInput
                      mask="000000"
                      name="card_first6"
                      value={formData.card_first6}
                      onAccept={value => updateField("card_first6", value)}
                      className={inputClass}
                      placeholder="123456"
                      autoComplete="off"
                    />
                    {errors.card_first6 && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {errors.card_first6}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass}>Card (Last 4)</label>
                    <IMaskInput
                      mask="0000"
                      name="card_last4"
                      value={formData.card_last4}
                      onAccept={value => updateField("card_last4", value)}
                      className={inputClass}
                      placeholder="1234"
                      autoComplete="off"
                    />
                    {errors.card_last4 && (
                      <p className="text-red-500 text-xs mt-1.5">
                        {errors.card_last4}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Details */}
            {step === 3 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                <div>
                  <label className={labelClass}>Describe the Issue</label>
                  <textarea
                    name="description"
                    rows={6}
                    maxLength={500}
                    value={formData.description}
                    onChange={e => {
                      updateField("description", e.target.value);
                      setCharCount(e.target.value.length);
                    }}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us what happened, when it occurred, and what resolution you're looking for..."
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-400">
                      At least 20 characters
                    </span>
                    <span
                      className={`text-xs font-medium ${charCount > 480 ? "text-red-500" : "text-gray-400"}`}
                    >
                      {charCount} / 500
                    </span>
                  </div>
                  {errors.description && (
                    <p className="text-red-500 text-xs mt-1.5">
                      {errors.description}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="bg-[#faf8f5] rounded-xl p-5 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 shrink-0">
                      <i className="ri-user-line text-gray-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                        Contact
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {formData.name}
                      </p>
                      <p className="text-sm text-gray-500">{formData.email}</p>
                    </div>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 shrink-0">
                      <i className="ri-bank-card-line text-gray-500 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                        Transaction
                      </p>
                      <p className="text-sm font-semibold text-gray-800">
                        {formData.currency} {formData.amount}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formData.date_of_transaction}
                      </p>
                      {formData.card_first6 && formData.card_last4 && (
                        <p className="text-sm text-gray-500">
                          Card: {formData.card_first6}******
                          {formData.card_last4}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-gray-200 shrink-0">
                      <i className="ri-file-text-line text-gray-500 text-sm"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                        Issue
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed mt-1">
                        {formData.description}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 text-center">
                  By submitting, you confirm that all information provided is
                  accurate.
                </p>
              </div>
            )}

            {/* Navigation */}
            <div
              className={`flex items-center gap-3 mt-8 pt-6 border-t border-gray-100 ${step === 4 ? "justify-center" : ""}`}
            >
              {step > 1 && step < 4 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-5 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back
                </button>
              )}
              {step > 1 && step === 4 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="absolute left-6 md:left-8 px-5 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back
                </button>
              )}
              {step < 4 && <div className="flex-1" />}
              {step < 4 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-8 py-4 bg-[#1a1a1a] text-white rounded-xl text-base font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  Continue
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-12 py-4 bg-[#1a1a1a] text-white rounded-xl text-base font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-3 disabled:opacity-50 shadow-sm"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg"></i>
                      Submit Dispute
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Need help?{" "}
          <a href="/contact-support" className="text-[#8b7355] hover:underline">
            Contact our support team
          </a>
        </p>
      </div>
    </PageLayout>
  );
}
